/**
 * Formato y parseo es-VE. Prohibido formatear montos a mano fuera de aquí y del
 * componente <Dinero> (skill diseno-visual-mg).
 */
import { Money, MoneyError } from './money.ts';

export type Moneda = 'USD' | 'BS';

export const SIMBOLO: Record<Moneda, string> = { USD: '$', BS: 'Bs' };

/** "$ 1.234,56" */
export function formatearUsd(m: Money): string {
  return `${SIMBOLO.USD} ${m.toDisplay()}`;
}

/** "Bs 996.721,45" */
export function formatearBs(m: Money): string {
  return `${SIMBOLO.BS} ${m.toDisplay()}`;
}

export function formatear(m: Money, moneda: Moneda): string {
  return moneda === 'USD' ? formatearUsd(m) : formatearBs(m);
}

/**
 * Parsea entrada de usuario en es-VE: "1.234,56" → 1234.56. Reglas:
 * - La coma es SIEMPRE el separador decimal; los puntos son miles y se descartan.
 * - "1234,5", "1.234", "-12,30", "$ 1.234,56", "Bs 10,00" son válidos.
 * - Si no hay coma, el punto NO se interpreta como decimal ("1.234" = 1234).
 */
export function parseEsVE(texto: string): Money {
  const limpio = texto
    .replace(/[$]|Bs\.?/gi, '')
    .replace(/\s+/g, '')
    .trim();
  if (limpio === '') throw new MoneyError('Monto vacío');
  const negativo = limpio.startsWith('-');
  const cuerpo = negativo ? limpio.slice(1) : limpio;
  const partes = cuerpo.split(',');
  if (partes.length > 2) throw new MoneyError(`Monto inválido: "${texto}"`);
  const entero = (partes[0] ?? '').replace(/\./g, '');
  const decimales = partes[1] ?? '';
  if (!/^\d+$/.test(entero) || (decimales !== '' && !/^\d+$/.test(decimales))) {
    throw new MoneyError(`Monto inválido: "${texto}"`);
  }
  return Money.parse(`${negativo ? '-' : ''}${entero}${decimales ? `.${decimales}` : ''}`);
}

/** Cantidades (unidades) también usan Money para evitar floats en multiplicaciones. */
export function formatearCantidad(m: Money, decimales = 2): string {
  const fijo = m.decimal.toFixed(decimales);
  const [ent = '0', dec] = fijo.split('.');
  const miles = ent.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const decLimpio = dec ? dec.replace(/0+$/, '') : '';
  return decLimpio ? `${miles},${decLimpio}` : miles;
}

/**
 * Tasas (Bs por $) con su precisión real: mínimo 2 decimales, hasta 8, sin ceros sobrantes.
 * La tasa BCV oficial trae 8 decimales; mostrarla recortada a 2 hacía que "$ × tasa" no cuadrara
 * a mano con los Bs del documento (observación del cliente 24-09-2026).
 */
export function formatearTasa(t: Money | string): string {
  const m = typeof t === 'string' ? Money.parse(t) : t;
  const fijo = m.decimal.toFixed(8);
  const [ent = '0', dec = ''] = fijo.split('.');
  const miles = ent.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const recortado = dec.replace(/0+$/, '').padEnd(2, '0');
  return `${miles},${recortado}`;
}

/**
 * Monto con todos sus decimales (hasta 8, los que guarda la base) y al menos 2, sin redondear a céntimos: «2.40»,
 * «0.585». Para lo que la norma no deja redondear a medio camino: las tasas de cambio y el IGTF en divisa (Resolución
 * BCV 21-08-01: el redondeo a dos decimales se aplica una sola vez).
 */
export function textoExacto(m: Money | string): string {
  const v = typeof m === 'string' ? Money.parse(m) : m;
  const fijo = v.decimal.toFixed(8);
  const [ent = '0', dec = ''] = fijo.split('.');
  return `${ent}.${dec.replace(/0+$/, '').padEnd(2, '0')}`;
}

/** Lo mismo para mostrar en es-VE: «0,585», «2,40», «1.234,5678». */
export function formatearExacto(m: Money | string): string {
  const [ent = '0', dec = ''] = textoExacto(m).split('.');
  const negativo = ent.startsWith('-');
  const miles = (negativo ? ent.slice(1) : ent).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${negativo ? '-' : ''}${miles},${dec}`;
}
